import { keepPreviousData } from '@tanstack/react-query';
import type {
    APPLICANT_ATTRIBUTION,
    ETA_ADVISER_PLACEHOLDERS_OBJECT,
    ETA_DOCUMENT_DEMANDED_PLACEHOLDERS_OBJECT,
    ETA_DOCUMENTS_DEMANDED_PLACEHOLDERS,
    ETA_PLACEHOLDER_LIST,
    ETA_PLACEHOLDER_TYPE,
    ETA_PLACEHOLDERS,
    ETA_PLACEHOLDERS_OBJECT,
    ETA_SIGNATURE_PLACEHOLDERS,
    ETA_TRACKING_PLACEHOLDERS_OBJECT,
    TEMPLATE_TYPE,
} from 'cpool-constants';
import {
    AGGREGATE_NAMES,
    APPLICANT_ATTRIBUTIONS,
    ETA_PLACEHOLDER,
    MAIL_CONTENT_KINDS,
    SALUTATIONS,
    TEMPLATE_RESOLVERS,
    TEMPLATE_TYPES,
} from 'cpool-constants';
import type {
    MailHeaderFooterPart,
    SchemaLoanRequestResolver_Get,
    SchemaTemplateResolver_GetDocumentsDemandedTemplate,
    SchemaTemplateResolver_GetSignature,
    SchemaUserResolver_Get,
} from 'cpool-tables/backend-resolvers';
import {
    schemaTemplateResolver_GetDocumentsDemandedTemplate,
    schemaTemplateResolver_GetSignature,
} from 'cpool-tables/backend-resolvers';
import type { ValueOf } from 'cpool-utils';
import { buildListAsHTML, buildListAsText, getTitleCase } from 'cpool-utils';

import { GENDERS_SHORTHAND } from '~/constants';
import { useOwnMailHeaderFooter } from '~/features/emailMessageDialog/hooks/useOwnMailHeaderFooter';
import type { InputKind } from '~/features/headerFooterTemplate/types';
import type { SchemaDemandedDocuments } from '~/features/leadDetails/schemas/demandedDocuments.schema';
import { useAggregateQuery } from '~/queryUtils/useQuery';
import { useQueryPositionStore } from '~/store/queryPositionStore';
import { renderPreviewEtaMailTemplate } from '~/utils/mailTemplates/renderPreviewEtaMailTemplate';
import { queryKeys } from '~/utils/queryKeys';

const useDemandedDocumentsPreview = ({
    attribution,
    contentKind,
    demandedDocuments,
    lead,
    user,
}: {
    lead?: SchemaLoanRequestResolver_Get | null;
    attribution: APPLICANT_ATTRIBUTION;
    demandedDocuments: SchemaDemandedDocuments;
    user?: SchemaUserResolver_Get;
    contentKind: InputKind;
}) => {
    const queryKey = queryKeys.getDemandedDocumentsTemplate();
    const queryPosition = useQueryPositionStore((state) => state.positions[JSON.stringify(queryKey)]);
    const template = useAggregateQuery({
        acl: { loanRequestId: lead?.id },
        aggregateName: AGGREGATE_NAMES.TEMPLATE,
        resolverName: TEMPLATE_RESOLVERS.getDemandedDocumentsTemplate,
        schema: schemaTemplateResolver_GetDocumentsDemandedTemplate,
        searchParams: { position: queryPosition },
        tanstack: {
            placeholderData: keepPreviousData,
        },
    });

    const signature = useAggregateQuery({
        acl: { loanRequestId: lead?.id },
        aggregateName: AGGREGATE_NAMES.TEMPLATE,
        resolverName: TEMPLATE_RESOLVERS.getSignature,
        schema: schemaTemplateResolver_GetSignature,
        searchParams: { id: user?.signatureId, position: queryPosition },
        tanstack: {
            enabled: !!user?.signatureId,
            placeholderData: keepPreviousData,
        },
    });

    const headerFooter = useOwnMailHeaderFooter();

    const isError = template.isError || signature.isError || headerFooter.isError;

    if (!template.data || !headerFooter.data || !lead) return { isError, preview: null };

    const prepare = (
        rawTemplate: NonNullable<
            | SchemaTemplateResolver_GetDocumentsDemandedTemplate
            | SchemaTemplateResolver_GetSignature
            | { data: MailHeaderFooterPart; type: TEMPLATE_TYPE }
        >,
        substitutions: Array<{ object: Record<string, string>; placeholder: ETA_PLACEHOLDER_LIST[number] }>,
    ) => {
        const etaContext = substitutions.reduce((acc, { object, placeholder }) => {
            acc[placeholder] = object[placeholder];
            return acc;
        }, {} as Record<ValueOf<ETA_PLACEHOLDER_TYPE>, string>);
        const { renderedText } = renderPreviewEtaMailTemplate({
            contentKind,
            etaContext,
            isOfferTemplate: false,
            template: rawTemplate.data[contentKind],
            templateType: rawTemplate.type,
        });
        return renderedText;
    };

    const personalData =
        attribution !== APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1 && lead.antragsteller2
            ? lead.antragsteller2.personendaten
            : lead.antragsteller1.personendaten;

    const preparedDocuments: Array<string> = Object.values(demandedDocuments).reduce(
        (acc: Array<string>, documents) => {
            acc.push(...documents.filter(({ requested }) => requested).map(({ name }) => name));
            return acc;
        },
        [],
    );

    // like getEtaTemplatePlaceholders, but also handles antragsteller2
    const placeholders = {
        [ETA_PLACEHOLDER.ANREDE_BERATER]:
            user?.gender === GENDERS_SHORTHAND.m
                ? getTitleCase(SALUTATIONS.HERR) ?? ''
                : getTitleCase(SALUTATIONS.FRAU) ?? '',
        [ETA_PLACEHOLDER.EMAIL_BERATER]: user?.email ?? '',
        [ETA_PLACEHOLDER.NACHNAME_BERATER]: user?.lastName ?? '',
        [ETA_PLACEHOLDER.TELEFON_BERATER]: user?.phone ?? '',
        [ETA_PLACEHOLDER.TITEL_BERATER]: user?.title ?? '',
        [ETA_PLACEHOLDER.VORNAME_BERATER]: user?.firstName ?? '',
    } satisfies ETA_ADVISER_PLACEHOLDERS_OBJECT;

    const trackingPlaceholders = {
        [ETA_PLACEHOLDER.APPLICANT_ID]: lead.antragsteller1.id,
        [ETA_PLACEHOLDER.FIRST_PARTY_ID]: lead.tracking?.fpid || '',
        [ETA_PLACEHOLDER.LINK_NAME]: '',
        [ETA_PLACEHOLDER.MAIL_ID]: template?.data.name || '',
        [ETA_PLACEHOLDER.MAIL_VERSION_ID]: template?.data.aggregateVersion?.toString() || '',
    } satisfies ETA_TRACKING_PLACEHOLDERS_OBJECT;

    const leadPlaceholders = {
        [ETA_PLACEHOLDER.NACHNAME]: personalData.nachname ?? '',
        [ETA_PLACEHOLDER.VORNAME]: personalData.vorname ?? '',
        [ETA_PLACEHOLDER.ANREDE]: getTitleCase(personalData.anrede) ?? '',
    } satisfies Pick<
        ETA_PLACEHOLDERS_OBJECT,
        ETA_PLACEHOLDER_TYPE['NACHNAME'] | ETA_PLACEHOLDER_TYPE['VORNAME'] | ETA_PLACEHOLDER_TYPE['ANREDE']
    >;

    const adviserSubstitutions = [
        { object: placeholders, placeholder: ETA_PLACEHOLDER.EMAIL_BERATER },
        { object: placeholders, placeholder: ETA_PLACEHOLDER.NACHNAME_BERATER },
        { object: placeholders, placeholder: ETA_PLACEHOLDER.TELEFON_BERATER },
        { object: placeholders, placeholder: ETA_PLACEHOLDER.VORNAME_BERATER },
        { object: placeholders, placeholder: ETA_PLACEHOLDER.ANREDE_BERATER },
        { object: placeholders, placeholder: ETA_PLACEHOLDER.TITEL_BERATER },
    ] satisfies Array<{
        placeholder: ETA_SIGNATURE_PLACEHOLDERS;

        object: Record<string, string>;
    }>;

    const documentsDemandedPlaceholders = {
        [ETA_PLACEHOLDER.URL]: 'https://dokumente.kredit.de/?pl=ABC123',
        [ETA_PLACEHOLDER.DOKUMENTE]:
            contentKind === MAIL_CONTENT_KINDS.MAIL_CONTENT
                ? buildListAsText(preparedDocuments)
                : buildListAsHTML(preparedDocuments),
    } satisfies Pick<ETA_DOCUMENT_DEMANDED_PLACEHOLDERS_OBJECT, 'url' | 'dokumente'>;

    const templateSubstitutions = [
        { object: leadPlaceholders, placeholder: ETA_PLACEHOLDER.NACHNAME },
        { object: leadPlaceholders, placeholder: ETA_PLACEHOLDER.VORNAME },
        {
            object: leadPlaceholders,
            placeholder: ETA_PLACEHOLDER.ANREDE,
        },
        ...adviserSubstitutions,
        {
            object: documentsDemandedPlaceholders,
            placeholder: ETA_PLACEHOLDER.URL,
        },
        {
            object: documentsDemandedPlaceholders,
            placeholder: ETA_PLACEHOLDER.DOKUMENTE,
        },
        { object: trackingPlaceholders, placeholder: ETA_PLACEHOLDER.APPLICANT_ID },
        { object: trackingPlaceholders, placeholder: ETA_PLACEHOLDER.FIRST_PARTY_ID },
        { object: trackingPlaceholders, placeholder: ETA_PLACEHOLDER.LINK_NAME },
        { object: trackingPlaceholders, placeholder: ETA_PLACEHOLDER.MAIL_ID },
        { object: trackingPlaceholders, placeholder: ETA_PLACEHOLDER.MAIL_VERSION_ID },
    ] satisfies Array<{
        placeholder: ETA_PLACEHOLDERS | ETA_DOCUMENTS_DEMANDED_PLACEHOLDERS;
        object: Record<string, string>;
    }>;

    const prepareHeaderFooter = (part: MailHeaderFooterPart | null, type: TEMPLATE_TYPE) =>
        part ? prepare({ data: part, type }, adviserSubstitutions) : [];

    return {
        isError,
        preview: [
            ...prepareHeaderFooter(headerFooter.data.header, TEMPLATE_TYPES.MAIL_HEADER),
            ...prepare(template.data, templateSubstitutions),
            ...(signature.data && user ? prepare(signature.data, adviserSubstitutions) : []),
            ...prepareHeaderFooter(headerFooter.data.footer, TEMPLATE_TYPES.MAIL_FOOTER),
        ],
    };
};

export { useDemandedDocumentsPreview };
